import { AxiosError } from 'axios'
import { computed, flow, makeObservable, observable, toJS } from 'mobx'

import { Statistic } from 'Shared/molecules/Statistic/types'
import { requestWrapper } from 'Shared/utils'

import { ALL_OPTION, STORE_UI } from 'Core/constants'
import { searchQueryConfig } from 'Core/constants/filters'
import { CoreUrlParams, CustomGenerator, SortingParams } from 'Core/interfaces'
import { TableStore } from 'Core/stores/tableStore/TableStore'
import { entityToStringLabelValue, getDefaultStoreArrayValue } from 'Core/utils'
import { storagePageSizeConstant } from 'Core/utils/storeHelpers/storagePageSizeConstant'

import { UrlParams } from 'Portals/Customer/constants'
import { Order } from 'Portals/Customer/entities'
import {
	getFiltersConfig,
	mainSearchConfig
} from 'Portals/Customer/Pages/Order/TableList/Filters/utils/config'
import { defaultStatistic } from 'Portals/Customer/Pages/Order/TableList/Table/utils/constant'
import { OrderRepo, ServiceSiteRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import { mergeStatistic } from 'Portals/Customer/utils'

export class OrderTable extends TableStore<Order.Basic> {
	@observable private statuses = getDefaultStoreArrayValue<Entity>()
	@observable private serviceSites = getDefaultStoreArrayValue<Entity>()
	@observable orderStatistic = getDefaultStoreArrayValue<Statistic>()

	constructor(public rootStore: RootStore) {
		super(storagePageSizeConstant('order'))
		makeObservable(this)
	}

	@computed
	get orders() {
		return toJS(this.items)
	}

	@computed
	get statusOptions(): LabelValue[] {
		const formatStatuses = this.statuses.value.map((status) => entityToStringLabelValue(status))

		return [ALL_OPTION, ...formatStatuses]
	}

	@computed
	get serviceSiteOptions(): LabelValue[] {
		const formatStatuses = this.serviceSites.value.map((status) =>
			entityToStringLabelValue(status)
		)

		return [ALL_OPTION, ...formatStatuses]
	}

	@flow.bound
	*getItems(defaultSort?: SortingParams) {
		try {
			yield this.requestData({
				defaultSort,
				repository: OrderRepo.getList
			})
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*retrieveStatuses() {
		yield requestWrapper({
			field: this.statuses,
			repository: OrderRepo.retrieveStatuses,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveServiceSites() {
		yield requestWrapper({
			field: this.serviceSites,
			repository: ServiceSiteRepo.retrieveServiceSites,
			repositoryParams: null
		})
	}

	retrieveFiltersData = () => {
		this.retrieveStatuses()
		this.retrieveServiceSites()
	}

	clearFilterData = () => {
		this.statuses = getDefaultStoreArrayValue<Entity>()
		this.serviceSites = getDefaultStoreArrayValue<Entity>()
	}

	initFromURLParams = (params: Record<string, string[]>) => {
		const searchKeys = [
			UrlParams.id,
			UrlParams.itemId,
			UrlParams.itemName,
			CoreUrlParams.searchQuery
		]

		const filterKeys = [
			UrlParams.startDate,
			UrlParams.endDate,
			UrlParams.statusId,
			UrlParams.serviceSiteId
		]

		const mergedSearchConfig = {
			...mainSearchConfig,
			...searchQueryConfig
		}

		this.initSearchParamsFromUrl(params, searchKeys, mergedSearchConfig)
		this.initFilterParamsFromUrl(params, filterKeys, getFiltersConfig(false))
	};

	@flow.bound
	*retrieveOrderStatistic(): CustomGenerator<Order.Statistic> {
		this.orderStatistic.isLoading = true
		try {
			const statistic = yield OrderRepo.retrieveOrderStatistic()
			const mappedStatistic = mergeStatistic(statistic, defaultStatistic)
			this.orderStatistic.value = mappedStatistic
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.orderStatistic.isLoading = false
	}
}
